// ── Display helpers for the exam coding workspace (EX-EXM02) ───────────────
// The student-facing task view (`GET .../coding/:questionId`) only ever sends
// `{ languageKey, languageVersion }` pairs — never a label or toolchain string,
// because the graded catalogue route (`GET /v1/judge-platform/runtimes`) is
// `problem.write`-gated and a student has no such permission. `docs/24` §3.1's
// decision 2 makes the *id* shared vocabulary between the runner's table and
// the Playground's bundled contract, so that table is a safe,
// no-extra-request source for display text — it is prose, not a security
// boundary, and a miss falls back to a generic formatter rather than crashing.
import { ApiError } from "@/lib/api";
import { getLanguageById } from "@/features/playground/config/languages";
import { getLanguageVersion } from "@/features/playground/config/backend-language-contract.generated";

export function formatRuntimeLanguageName(languageKey: string): string {
  return getLanguageById(languageKey)?.name ?? languageKey;
}

export function getRuntimeMonacoLanguage(languageKey: string): string {
  return getLanguageById(languageKey)?.monacoLanguage ?? "plaintext";
}

export function getRuntimeDefaultSourceCode(languageKey: string): string {
  return getLanguageById(languageKey)?.defaultCode ?? "";
}

/** Mirrors the backend's own fallback (`graded-runtime.service.ts`) for a version this table doesn't carry. */
function fallbackVersionLabel(languageKey: string, languageVersion: string): string {
  if (languageKey === "cpp") return `C++${languageVersion.replace(/^c\+\+/, "")}`;
  if (languageKey === "c") return languageVersion.toUpperCase();
  if (languageKey === "python") return `Python ${languageVersion}`;
  if (languageKey === "javascript") return `Node.js ${languageVersion}`;
  return `${languageKey} ${languageVersion}`;
}

export function formatRuntimeVersionLabel(languageKey: string, languageVersion: string): string {
  return (
    getLanguageVersion(languageKey, languageVersion)?.label ??
    fallbackVersionLabel(languageKey, languageVersion)
  );
}

/**
 * Stable backend error codes → coding-task i18n keys, the same convention
 * `features/playground/lib/api-error-message.ts` established. Unmapped codes
 * fall back to the server's own message.
 */
const CODE_TO_MESSAGE_KEY: Readonly<Record<string, string>> = {
  JUDGE_ENGINE_BUSY: "errorJudgeBusy",
  JUDGE_LANGUAGE_NOT_AVAILABLE: "errorLanguageNotAvailable",
  JUDGE_QUEUE_USER_LIMIT: "errorQueueUserLimit",
  EXAM_CODING_TASK_NOT_FOUND: "errorTaskNotFound",
  EXAM_CODING_SESSION_CLOSED: "errorSessionClosed",
  EXAM_CODING_SUBMIT_LIMIT: "errorSubmitLimit",
  EXAM_CODING_TASK_INVALID: "errorTaskInvalid",
  RATE_LIMITED: "errorRateLimited",
};

export function codingTaskErrorMessageKey(err: unknown): string | null {
  if (!(err instanceof ApiError) || !err.code) return null;
  return CODE_TO_MESSAGE_KEY[err.code] ?? null;
}

/** `true` for `JUDGE_ENGINE_BUSY` — a run refused only because grading won the box, safe to retry. */
export function isJudgeEngineBusy(err: unknown): boolean {
  return err instanceof ApiError && err.code === "JUDGE_ENGINE_BUSY";
}
